import { Field } from '@atlaskit/form';
import { Text } from '@atlaskit/primitives';
import DatabasePicker from '@databases/content-picker/DatabasePicker';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  type BrokenDatabase,
  type Database,
  type DatabaseField,
  type DatabaseFieldType
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type EntryLinkFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.ENTRY_LINK>;
};

const EntryLinkFieldSettings: FunctionComponent<EntryLinkFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectedDatabase = defaultStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdOptional(state, {
      contentId: field.settings.contentId ?? ''
    })
  );

  const handleDatabaseChange = useCallback(
    (database: Database | BrokenDatabase | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_DATABASE_ID,
          fieldId: field.id,
          databaseSpace: database ? { key: database.space.key } : undefined,
          databaseId: database && 'id' in database ? database.id : undefined,
          contentId: database?.contentId
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const displayEntryLinkFromLabel = formatMessage(messages.entryLinkDisplaySourceLabel);

  return (
    <Field
      label={
        <Text size="small" weight="bold" color="color.text.subtle">
          {displayEntryLinkFromLabel}
        </Text>
      }
      id="entry-link-source-database-picker-input"
      name="entry-link-source-database-field"
      isRequired
    >
      {({ fieldProps }) => (
        <DatabasePicker
          inputId={fieldProps.id}
          isCompact
          onChange={handleDatabaseChange}
          selectedDatabase={selectedDatabase}
        />
      )}
    </Field>
  );
};

export default EntryLinkFieldSettings;
